import React, { useState, useEffect, useRef, useCallback } from "react";
import { socket } from "../socket";
import { PlayerAvatar, PlayerName, bannerStyle } from "./PlayerIdentity";
import { t } from "../i18n";
import { useSounds, useCountdownTick } from "../sound";
import { alertDialog } from "../dialog";

// Pictionary : un joueur dessine, les autres devinent dans le chat.
//
// La toile a une résolution fixe et tous les clients rejouent la même liste de
// traits dans le même ordre. C'est ce qui rend le pot de peinture fiable : un
// remplissage dépend des pixels voisins, il ne donnerait pas le même résultat
// sur deux toiles de tailles différentes.
const CANVAS = { w: 800, h: 450 };
const COLORS = [
  "#ffffff", "#c1c1c1", "#4c4c4c", "#000000", "#ef4444", "#fbd200", "#d68ac9", "#1a5c1a",
  "#101a5c", "#1a68a8", "#22c9f0", "#3b3bdb", "#e06f10", "#f08a1a", "#22c55e", "#8a5a3c",
];
const SIZES = [3, 7, 14, 24];
const TOOLS = [
  { id: "pen",    icon: "bi-pen-fill" },
  { id: "eraser", icon: "bi-eraser-fill" },
  { id: "fill",   icon: "bi-paint-bucket" },
];
// Les traits partent par petits paquets pour que les autres voient le geste se
// tracer plutôt que d'apparaître d'un coup à la levée du stylo.
const FLUSH_MS = 60;

function PictionaryGame({ gameId, player }) {
  const [state, setState] = useState(null);
  const [chat, setChat]   = useState([]);
  const [draft, setDraft] = useState("");
  const [tool, setTool]   = useState("pen");
  const [color, setColor] = useState("#000000");
  const [size, setSize]   = useState(SIZES[1]);
  const [now, setNow]     = useState(Date.now());
  const [reveal, setReveal] = useState(null);

  const canvasRef = useRef(null);
  const ctxRef    = useRef(null);
  const strokesRef = useRef([]);
  const drawing   = useRef(false);
  const pending   = useRef([]);
  const groupRef  = useRef(0);
  const flushRef  = useRef(null);
  const chatEndRef = useRef(null);

  const myId = player?.playerId;
  const isDrawer = state?.drawerId === myId;
  const { playPop, playSuccess } = useSounds();

  // ── Rendu ──────────────────────────────────────────────────────────────────
  const paintStroke = useCallback((ctx, s) => {
    if (s.t === "fill") return floodFill(ctx, s.x, s.y, s.c);
    ctx.strokeStyle = s.t === "eraser" ? "#ffffff" : s.c;
    ctx.lineWidth = s.w;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(s.p[0], s.p[1]);
    for (let i = 2; i < s.p.length; i += 2) ctx.lineTo(s.p[i], s.p[i + 1]);
    if (s.p.length === 2) ctx.lineTo(s.p[0] + 0.1, s.p[1] + 0.1);  // simple point
    ctx.stroke();
  }, []);

  const redraw = useCallback((strokes) => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, CANVAS.w, CANVAS.h);
    for (const s of strokes) paintStroke(ctx, s);
  }, [paintStroke]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    ctxRef.current = c.getContext("2d", { willReadFrequently: true });
    redraw(strokesRef.current);
  }, [redraw, state?.phase]);

  // ── Réseau ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    socket.on("piState", (s) => {
      setState(s);
      setChat(s.chat || []);
      strokesRef.current = s.strokes || [];
      redraw(strokesRef.current);
      if (s.phase !== "reveal") setReveal(null);
    });
    socket.on("piStroke", ({ stroke }) => {
      strokesRef.current.push(stroke);
      if (ctxRef.current) paintStroke(ctxRef.current, stroke);
    });
    socket.on("piStrokes", ({ strokes }) => {
      strokesRef.current = strokes;
      redraw(strokes);
    });
    socket.on("piChat", (m) => setChat(c => [...c.slice(-59), m]));
    socket.on("piPlayers", ({ players }) => setState(s => s ? { ...s, players } : s));
    socket.on("piHint", ({ mask }) => setState(s => s ? { ...s, mask } : s));
    socket.on("piYouGuessed", ({ word }) => { playSuccess(); setState(s => s ? { ...s, word } : s); });
    socket.on("piReveal", (r) => {
      playPop();
      setReveal(r);
      setState(s => s ? { ...s, phase: "reveal", word: r.word, players: r.players } : s);
    });
    socket.on("piError", ({ error }) => alertDialog(error));

    socket.emit("piGetState", { gameId });
    return () => {
      ["piState", "piStroke", "piStrokes", "piChat", "piPlayers", "piHint",
       "piYouGuessed", "piReveal", "piError"].forEach(e => socket.off(e));
    };
  }, [gameId, redraw, paintStroke, playPop, playSuccess]);

  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ block: "nearest" }); }, [chat]);

  // ── Dessin ─────────────────────────────────────────────────────────────────
  const canDraw = isDrawer && state?.phase === "draw";

  const posOf = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return {
      x: Math.round((e.clientX - r.left) * (CANVAS.w / r.width)),
      y: Math.round((e.clientY - r.top) * (CANVAS.h / r.height)),
    };
  };

  const flush = useCallback(() => {
    if (pending.current.length < 2) return;
    const stroke = { t: tool, c: color, w: size, g: groupRef.current, p: pending.current };
    socket.emit("piStroke", { gameId, stroke });
    strokesRef.current.push(stroke);
    // On garde le dernier point : sans lui, deux paquets successifs laisseraient
    // un trou visible entre eux.
    const last = pending.current.slice(-2);
    pending.current = last;
  }, [gameId, tool, color, size]);

  const onDown = (e) => {
    if (!canDraw) return;
    e.preventDefault();
    const { x, y } = posOf(e);
    if (tool === "fill") {
      const stroke = { t: "fill", c: color, x, y, g: ++groupRef.current };
      socket.emit("piStroke", { gameId, stroke });
      strokesRef.current.push(stroke);
      paintStroke(ctxRef.current, stroke);
      return;
    }
    drawing.current = true;
    groupRef.current += 1;
    pending.current = [x, y];
    e.currentTarget.setPointerCapture?.(e.pointerId);
    flushRef.current = setInterval(flush, FLUSH_MS);
  };

  const onMove = (e) => {
    if (!drawing.current) return;
    const { x, y } = posOf(e);
    const p = pending.current;
    if (p.length >= 2 && p[p.length - 2] === x && p[p.length - 1] === y) return;
    p.push(x, y);
    const ctx = ctxRef.current;
    ctx.strokeStyle = tool === "eraser" ? "#ffffff" : color;
    ctx.lineWidth = size; ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(p[p.length - 4], p[p.length - 3]);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const onUp = () => {
    if (!drawing.current) return;
    drawing.current = false;
    clearInterval(flushRef.current);
    flush();
    pending.current = [];
  };

  useEffect(() => () => clearInterval(flushRef.current), []);

  const left = state?.endsAt ? Math.max(0, Math.ceil((state.endsAt - now) / 1000)) : 0;
  useCountdownTick(left, { active: state?.phase === "draw" });

  // ── Affichage ──────────────────────────────────────────────────────────────
  if (!state) return <div className="Pictionary"><p className="pi-waiting">{t("pictionary.waiting")}</p></div>;

  const drawer = state.players?.find(p => p.playerId === state.drawerId);
  const iGuessed = state.players?.find(p => p.playerId === myId)?.guessed;

  const send = (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    socket.emit("piChat", { gameId, text: draft });
    setDraft("");
  };

  return (
    <div className="Pictionary">
      <div className="pi-top">
        <span className="pi-round">{t("pictionary.roundOf", { round: state.round + 1, total: state.totalRounds })}</span>
        <span className="pi-timer">{left}</span>
        <span className="pi-word">
          {state.phase === "choose"
            ? (isDrawer ? t("pictionary.pickWord") : t("pictionary.waitingPick", { pseudo: drawer?.pseudo || "?" }))
            : (state.word || state.mask)}
        </span>
      </div>

      <div className="pi-layout">
        <aside className="pi-players">
          {[...(state.players || [])].sort((a, b) => b.score - a.score).map(p => (
            <div key={p.playerId} style={bannerStyle(p)}
              className={`pi-player${p.playerId === myId ? " me" : ""}${p.drawing ? " drawing" : ""}${p.guessed ? " guessed" : ""}`}>
              <PlayerAvatar player={p} size={34} />
              <div className="pi-player-info">
                <PlayerName player={p} className="pi-player-name" />
                <span className="pi-player-score">{t("pictionary.points", { n: p.score })}</span>
              </div>
            </div>
          ))}
        </aside>

        <main className="pi-main">
          <div className="pi-canvas-wrap">
            <canvas ref={canvasRef} width={CANVAS.w} height={CANVAS.h}
              className={`pi-canvas${canDraw ? " drawable" : ""}`}
              onPointerDown={onDown} onPointerMove={onMove}
              onPointerUp={onUp} onPointerLeave={onUp} onPointerCancel={onUp} />
            {state.phase === "choose" && isDrawer && (
              <div className="pi-choices">
                {(state.choices || []).map((w, i) => (
                  <button key={i} className="pi-choice"
                    onClick={() => socket.emit("piPickWord", { gameId, index: i })}>{w}</button>
                ))}
              </div>
            )}
            {reveal && (
              <div className="pi-reveal">
                <p className="pi-reveal-word">{reveal.word}</p>
                <p className="pi-reveal-sub">{t("pictionary.foundBy", { n: reveal.guessedCount })}</p>
              </div>
            )}
          </div>

          {canDraw && (
            <div className="pi-tools">
              <div className="pi-palette">
                {COLORS.map(c => (
                  <button key={c} className={`pi-swatch${color === c ? " on" : ""}`}
                    style={{ background: c }} onClick={() => setColor(c)} aria-label={c} />
                ))}
              </div>
              <div className="pi-tool-group">
                {TOOLS.map(tl => (
                  <button key={tl.id} className={`pi-tool${tool === tl.id ? " on" : ""}`}
                    onClick={() => setTool(tl.id)} title={t(`pictionary.tool.${tl.id}`)}>
                    <i className={`bi ${tl.icon}`}></i>
                  </button>
                ))}
                <button className="pi-tool" title={t("pictionary.tool.clear")}
                  onClick={() => socket.emit("piClear", { gameId })}>
                  <i className="bi bi-trash-fill"></i>
                </button>
                <button className="pi-tool" title={t("pictionary.tool.undo")}
                  onClick={() => socket.emit("piUndo", { gameId })}>
                  <i className="bi bi-arrow-counterclockwise"></i>
                </button>
              </div>
              <div className="pi-tool-group">
                {SIZES.map(s => (
                  <button key={s} className={`pi-size${size === s ? " on" : ""}`} onClick={() => setSize(s)}>
                    <span style={{ width: s, height: s }} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </main>

        <aside className="pi-chat">
          <div className="pi-chat-log">
            {chat.map((m, i) => (
              <p key={i} className={`pi-msg${m.found ? " found" : ""}${m.secret ? " secret" : ""}`}>
                {m.found
                  ? t("pictionary.hasGuessed", { pseudo: m.pseudo })
                  : <><b>{m.pseudo} :</b> {m.text}</>}
              </p>
            ))}
            <div ref={chatEndRef} />
          </div>
          <form onSubmit={send}>
            <input className="pi-chat-input" value={draft} onChange={e => setDraft(e.target.value)}
              autoComplete="off"
              placeholder={isDrawer ? t("pictionary.youDraw")
                : iGuessed ? t("pictionary.alreadyFound")
                : t("pictionary.chatPlaceholder")} />
          </form>
        </aside>
      </div>
    </div>
  );
}

// Remplissage par diffusion. Volontairement simple : la toile est petite et
// à résolution fixe, donc le coût reste négligeable et le résultat identique
// sur tous les clients.
function floodFill(ctx, x, y, hex) {
  const { w, h } = CANVAS;
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const at = (px, py) => (py * w + px) * 4;
  const start = at(x, y);
  const target = [d[start], d[start + 1], d[start + 2], d[start + 3]];
  const fill = [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16), 255];
  if (target.every((v, i) => v === fill[i])) return;
  const close = (i) => Math.abs(d[i] - target[0]) < 24 && Math.abs(d[i + 1] - target[1]) < 24
    && Math.abs(d[i + 2] - target[2]) < 24 && Math.abs(d[i + 3] - target[3]) < 24;
  const stack = [[x, y]];
  while (stack.length) {
    const [cx, cy] = stack.pop();
    if (cx < 0 || cy < 0 || cx >= w || cy >= h) continue;
    const i = at(cx, cy);
    if (!close(i)) continue;
    d[i] = fill[0]; d[i + 1] = fill[1]; d[i + 2] = fill[2]; d[i + 3] = fill[3];
    stack.push([cx + 1, cy], [cx - 1, cy], [cx, cy + 1], [cx, cy - 1]);
  }
  ctx.putImageData(img, 0, 0);
}

export default PictionaryGame;
